import Theme from "../../../../../../styles/Theme/Theme";
import { handleNotification } from "../../../../../../utils/config/notificationHandler";
import { buildOptionsForSelect } from "../../../../../../utils/hooks/serialization";

export const filtersTypes = {
    type: 'type',
    family: 'family'
}

/**
 * Get the filter not selected
 * @param {*} selectedFilter Object like {selected: 'type', value: 'value'}
 * @returns what filter is not selected
 */
export const getFilterNotSelected = (selectedFilter) => {
    switch (selectedFilter.selected) {
        case filtersTypes.type:
            return filtersTypes.family;
        case filtersTypes.family:
            return filtersTypes.type;
        default:
            return null;
    }
}

/**
 * Get the default value of the filter
 * @param {*} selectedFilterValue The value of the filter selected: 'type' or 'family'
 * @param {*} typeOptions Options for the type filter
 * @param {*} familyOptions Options for the family filter
 * @returns the default value for the filter selected
 */
export const getDefaultFilterValue = (selectedFilterValue, typeOptions, familyOptions) => {
    switch (selectedFilterValue) {
        case filtersTypes.type:
            return typeOptions[0]?.value;
        case filtersTypes.family:
            return familyOptions[0]?.value;
        default:
            return null;
    }

}

/**
 * Returns the filter object for the API based on the selected filter.
 * @param {object} selectedFilter - The selected filter object.
 * @returns {object|null} - The filter object for the API or null if no filter is selected.
 */
export const getFamilyTypeFilterForApi = (selectedFilter) => {
    switch (selectedFilter.selected) {
        case filtersTypes.type:
            return { types: selectedFilter.value };
        case filtersTypes.family:
            return { families: selectedFilter.value };
        default:
            return null;
    }
}

// ---------------------------- Serializations of properties ----------------------------

export const keysToExclude = ["crud_status", "label", "id", "ID"];
export const mandatoryFirstProperties = ['name', 'tenant', 'family', 'class'];

/**
    * Transforms the item data to a more readable format
    * @param {*} itemData Item data to be transformed
    * @param {*} itemProperties Item properties to be used in the transformation
    * @param {*} isToEdit If the item is to be edited
    * @returns {{ mandatoryProperties: [{property: prop1, value: value1}, {property: prop2, value: value2}],
* optionalProperties: [{property: prop3, value: value3}, {property: prop4, value: value4}]}} Transformed item data 
*/
export const transformItemData = (itemData, itemProperties, isToEdit = false) => {
    if (!itemData || !itemProperties) return null;
    const transformedData = {
        mandatoryProperties: [],
        optionalProperties: [],
        itemId: null,
    };

    const { mandatoryProperties, optionalProperties } = getListOfMandatoryAndOptionalProperties(itemProperties, ["federation"]);

    mandatoryProperties.forEach(prop => {
        transformedData.mandatoryProperties.push({
            ...prop,
            value: itemData[prop.property]
        });

    });

    optionalProperties.forEach(prop => {
        transformedData.optionalProperties.push({
            ...prop,
            value: itemData[prop.property]
        });
    });

    if (isToEdit) {
        transformedData.itemId = itemData.id;
    }

    const propertiesPrioritized = prioritizeMandatoryProperties(transformedData, mandatoryFirstProperties);

    return propertiesPrioritized;
};

/**
 * Transform the item properties data to a more readable format
 * format: {mandatoryProperties: [prop1, prop2], optionalProperties: [prop3, prop4]}
 * @param {*} itemProperties Item properties data to be transformed
 * @param {*} extraKeysToExclude Extra keys to be excluded from the transformation
 * @returns Transformed item properties data
 */
export const getListOfMandatoryAndOptionalProperties = (itemProperties, extraKeysToExclude = []) => {

    const result = { mandatoryProperties: [], optionalProperties: [] };

    const allKeysToExclude = [...keysToExclude, ...extraKeysToExclude];

    itemProperties.forEach(item => {
        item["Mandatory Properties"].forEach(prop => {
            if (allKeysToExclude.includes(prop.property)) return;
            result.mandatoryProperties.push(prop);
        });

        item["Optional Properties"].forEach(prop => {
            if (allKeysToExclude.includes(prop.property)) return;
            result.optionalProperties.push(prop);
        });
    });

    return result;
}

/**
 * Find the properties of a specific type in the properties data
 * @param {*} type The type of the properties to be found
 * @param {*} properties The properties data to be searched
 * @returns The properties of the specified type
 */
export const findPropertiesByType = (type, properties) => {
    return properties.find(propertyGroup => propertyGroup["Type"] === type);
}

/**
 * Find the properties of a specific class in the properties data
 * @param {*} classValue The class of the properties to be found
 * @param {*} properties The properties data to be searched
 * @returns The properties of the specified class
 */
export const findPropertiesByClass = (classValue, properties) => {
    return properties.find(propertyGroup => propertyGroup["Class"] === classValue);
}

/**
     * Check if the property is family, class or tenant
     * @param {*} property 
     * @returns 
     */
export const isFamilyOrClassOrTenantProperty = (property) => {
    return property === "family" || property === "class" || property === "tenant";
}


/**
 * Get the field type of the record
 * @param {*} record 
 * @returns 
 */
export const getFieldType = (record) => {
    switch (record.type) {
        case "select":
            return "SingleSelect";
        case 'int':
            return "Number";
        case 'list':
            return "TagSelect";
        default:
            return "TextBox";
    }
}

/**
  * Prioritize the mandatory properties in the transformed data
  * Putting then in the beginning of the object
  * @param {*} transformedData 
  * @param {*} mandatoryFirstProperties 
  * @returns Transformed data with the mandatory properties prioritized
  */
export const prioritizeMandatoryProperties = (transformedData, mandatoryFirstProperties) => {
    const { mandatoryProperties, optionalProperties } = transformedData;

    const prioritizedMandatory = mandatoryFirstProperties
        .map(property => mandatoryProperties.find(item => item.property === property))
        .filter(Boolean);

    const remainingMandatory = mandatoryProperties.filter(
        item => !mandatoryFirstProperties.includes(item.property)
    );

    const newMandatoryProperties = [...prioritizedMandatory, ...remainingMandatory];

    return {
        ...transformedData,
        mandatoryProperties: newMandatoryProperties,
    };
}


// ---------------------------- Create and Edit Modals ----------------------------
const labelStyle = {
    color: Theme.color['coolgray-10'], fontSize: Theme.typography.fontSize['text_md_desktop'],
    fontWeight: Theme.typography.weight['bold'], fontFamily: Theme.typography.fontFamily
}

/**
 * Build record for modal edit popup
 * @param {*} record
 * @returns
 */
const buildRecord = (record, t) => {
    return {
        ...record,
        key: record.property,
        fieldTypeOf: record?.fieldType,
        required: record?.required,
        size: record?.size ?? 12,
        name: record?.property,
        description: getDescriptionForProperty(record.property, t),
    }
}

/**
 * Serialize record for create and edit modal
 * @param {*} record Record to be serialized
 * @returns Serialized record
 */
export const serializeRecordForModal = (record, t) => {
    let result = [];

    result.push(buildRecord({ property: t("Mandatory Properties"), fieldType: "Label", size: 24, style: labelStyle }, t));

    record.mandatoryProperties && Object.keys(record.mandatoryProperties).forEach(key => {
        result.push(buildRecord(record.mandatoryProperties[key], t));
    });

    result.push(buildRecord({ property: t("Optional Properties"), fieldType: "Label", size: 24, style: labelStyle }, t));

    record.optionalProperties && Object.keys(record.optionalProperties).forEach(key => {
        result.push(buildRecord(record.optionalProperties[key], t));
    });

    return result;
}

/**
 * Build the property for the modal
 * @param {*} property Property to be built
 * @param {*} isToEdit If the property is to be edited
 * @param {*} isMandatory If the property is mandatory
 * @returns Built property
 */
export const buildPropertyForModal = (property, isToEdit, isMandatory = false) => {
    return {
        ...property,
        fieldType: getFieldType(property),
        required: isMandatory,
        options: buildOptionsForSelect(property.options),
        rule: property.regex,
        disabled: isToEdit ? isMandatory : false,
        value: property.value,
    }
}


/**
 * Get the description for the property
 * @param {*} propertyName Name of the property
 * @returns Description for the property
 */
const getDescriptionForProperty = (propertyName, t) => {
    if (propertyName === "federation") {
        return t("Create Item Federation Description");
    } else if (propertyName === "tenant") {
        return t("Create Item Tenant Description");
    } else if (propertyName === "family") {
        return t("Create Item Family Description");
    } else if (propertyName === "class") {
        return t("Create Item Class Description");
    }
}

/**
 * Handle the notification for the item
 * Used for create and edit items
 * @param {*} isFetching 
 * @param {*} isSuccess 
 * @param {*} isError 
 * @param {*} error 
 * @param {*} isToEdit 
 * @param {*} t 
 */
export const handleNotificationForItem = (isFetching, isSuccess, isError, error, isToEdit, t) => {
    const successMessage = isToEdit ? 'Edit Item Success' : 'Create Item Success';
    const errorMessage = isToEdit ? 'Edit Item Error' : 'Create Item Error';
    handleNotification(isFetching, isSuccess, isError, error, successMessage, errorMessage, t);
}

/**
 * Handle the notification for the relation
 * Used for create and remove relations
 * @param {*} isFetching 
 * @param {*} isSuccess 
 * @param {*} isError 
 * @param {*} error 
 * @param {*} t 
 * @param {*} isToCreate 
 */
export const handleNotificationForRelation = (isFetching, isSuccess, isError, error, t, isToCreate) => {
    const successMessage = isToCreate ? 'Create Relation Success' : 'Remove Relation Success';
    const errorMessage = isToCreate ? 'Create Relation Error' : 'Remove Relation Error';
    handleNotification(isFetching, isSuccess, isError, error, successMessage, errorMessage, t);
}
